import { X } from "lucide-react";
import type { CrossMatchNotification } from "@/hooks/useMatchHub";
import { useLanguage } from "@/hooks/useLanguage";

interface Props {
  toasts: CrossMatchNotification[];
  onOpen: (n: CrossMatchNotification) => void;
  onDismiss: (id: string) => void;
}

export function CrossMatchToast({ toasts, onOpen, onDismiss }: Props) {
  const { t } = useLanguage();
  if (!toasts.length) return null;
  return (
    <div
      className="pointer-events-none fixed z-40 flex w-[min(92vw,360px)] flex-col gap-2 px-3
                 bottom-[calc(env(safe-area-inset-bottom,0px)+72px)] left-1/2 -translate-x-1/2
                 lg:left-auto lg:right-4 lg:bottom-6 lg:translate-x-0"
      aria-live="polite"
    >
      {toasts.map((n) => (
        <div
          key={n.id}
          className="pointer-events-auto relative overflow-hidden rounded-2xl border border-border/60 glass px-4 py-3 shadow-[0_20px_60px_-15px_oklch(0_0_0/0.6)] animate-fade-in"
        >
          <div className="flex items-start gap-3">
            <div className="flex flex-col leading-tight">
              <div className="text-[11px] font-bold tracking-wide text-foreground">{n.title}</div>
              <div className="text-[10px] text-muted-foreground">{n.subtitle}</div>
            </div>
            <button
              onClick={() => onDismiss(n.id)}
              aria-label={t("close")}
              className="ml-auto rounded-full p-1 text-muted-foreground hover:bg-accent"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="mt-2 flex gap-2">
            <button
              onClick={() => onOpen(n)}
              className="flex-1 rounded-lg bg-gradient-to-r from-[oklch(0.58_0.24_27)] to-[oklch(0.7_0.22_15)] px-3 py-1.5 text-[11px] font-bold text-primary-foreground transition hover:opacity-90"
            >
              {t("open_now")}
            </button>
            <button
              onClick={() => onDismiss(n.id)}
              className="rounded-lg px-3 py-1.5 text-[11px] font-bold text-muted-foreground glass hover:bg-accent"
            >
              {t("ignore")}
            </button>
          </div>
          <span className="absolute inset-x-0 bottom-0 h-[2px] bg-gradient-to-r from-primary via-primary/60 to-transparent" />
        </div>
      ))}
    </div>
  );
}
